import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { importReceipts, importExpenses, importGuests, downloadGuestImportTemplate, importElectricity, downloadElectricityTemplate, downloadReceiptsImportTemplate, downloadExpensesImportTemplate } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Download, Users, Zap } from 'lucide-react';

export const ImportData = () => {
  const { hasRole } = useAuth();
  const [receiptsFile, setReceiptsFile] = useState(null);
  const [expensesFile, setExpensesFile] = useState(null);
  const [guestsFile, setGuestsFile] = useState(null);
  const [electricityFile, setElectricityFile] = useState(null);
  const [importingType, setImportingType] = useState(null); // 'receipts' | 'expenses' | 'guests' | 'electricity' | null
  const [results, setResults] = useState(null);
  const receiptsInputRef = useRef(null);
  const expensesInputRef = useRef(null);
  const guestsInputRef = useRef(null);
  const electricityInputRef = useRef(null);

  const canImport = hasRole(['admin', 'manager', 'accountant']);
  const isAdmin = hasRole(['admin']);

  const handleReceiptsImport = async () => {
    if (!receiptsFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setImportingType('receipts');
      const response = await importReceipts(receiptsFile);
      setResults({ type: 'receipts', ...response.data });
      if (response.data.success) {
        toast.success(response.data.message);
        setReceiptsFile(null);
        if (receiptsInputRef.current) receiptsInputRef.current.value = '';
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setResults({ type: 'receipts', success: false, message: err.response?.data?.detail || 'Import failed', errors: [] });
    } finally {
      setImportingType(null);
    }
  };

  const handleExpensesImport = async () => {
    if (!expensesFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setImportingType('expenses');
      const response = await importExpenses(expensesFile);
      setResults({ type: 'expenses', ...response.data });
      if (response.data.success) {
        toast.success(response.data.message);
        setExpensesFile(null);
        if (expensesInputRef.current) expensesInputRef.current.value = '';
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setResults({ type: 'expenses', success: false, message: err.response?.data?.detail || 'Import failed', errors: [] });
    } finally {
      setImportingType(null);
    }
  };

  const handleGuestsImport = async () => {
    if (!guestsFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setImportingType('guests');
      const response = await importGuests(guestsFile);
      setResults({ type: 'guests', ...response.data });
      if (response.data.success) {
        toast.success(response.data.message);
        setGuestsFile(null);
        if (guestsInputRef.current) guestsInputRef.current.value = '';
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setResults({ type: 'guests', success: false, message: err.response?.data?.detail || 'Import failed', errors: [] });
    } finally {
      setImportingType(null);
    }
  };

  const handleElectricityImport = async () => {
    if (!electricityFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setImportingType('electricity');
      const response = await importElectricity(electricityFile);
      setResults({ type: 'electricity', ...response.data });
      if (response.data.success) {
        toast.success(response.data.message);
        setElectricityFile(null);
        if (electricityInputRef.current) electricityInputRef.current.value = '';
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setResults({ type: 'electricity', success: false, message: err.response?.data?.detail || 'Import failed', errors: [] });
    } finally {
      setImportingType(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadGuestImportTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Guest_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleDownloadElectricityTemplate = async () => {
    try {
      const response = await downloadElectricityTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Electricity_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleDownloadReceiptsTemplate = async () => {
    try {
      const response = await downloadReceiptsImportTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Receipt_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Receipt template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleDownloadExpensesTemplate = async () => {
    try {
      const response = await downloadExpensesImportTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Payment_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Payment template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  if (!canImport) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-bold">Access Restricted</h2>
          <p className="text-gray-500 mt-2">Only Admin, Manager, or Accountant can import data.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="import-data-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Import Data</h1>
        <p className="text-slate-500 mt-1">Import guests, receipts and payments from Excel files</p>
      </div>

      {/* Import Results */}
      {results && (
        <Card className={results.success ? 'border-green-500 border-2' : 'border-red-500 border-2'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {results.success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              Import {results.type === 'receipts' ? 'Receipts' : results.type === 'expenses' ? 'Payments' : results.type === 'electricity' ? 'Electricity' : 'Guests'} - {results.success ? 'Success' : 'Failed'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium">{results.message}</p>
            {results.imported_count > 0 && (
              <p className="text-sm text-gray-600 mt-1">Imported: {results.imported_count} entries</p>
            )}
            {results.errors?.length > 0 && (
              <div className="mt-4">
                <p className="font-medium text-red-600">Errors ({results.errors.length}):</p>
                <ul className="list-disc list-inside text-sm text-red-500 mt-2 max-h-40 overflow-y-auto">
                  {results.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Guest List - Featured */}
      {isAdmin && (
        <Card className="dash-animate border-2 border-blue-500 bg-blue-50/30 hover:shadow-md transition-all duration-200" style={{ animationDelay: '0ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Users className="h-6 w-6" />
              Import Guest List
            </CardTitle>
            <CardDescription className="text-blue-600">
              Quickly import existing guests with room assignment, deposit, due date and rent
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-white rounded-lg border">
              <h4 className="font-medium text-sm mb-2">Excel Format (Column Order):</h4>
              <div className="grid grid-cols-9 gap-1 text-xs bg-slate-100 p-2 rounded font-mono overflow-x-auto">
                <div className="font-bold text-blue-700">Room*</div>
                <div className="font-bold">Sharing</div>
                <div className="font-bold">Status</div>
                <div className="font-bold text-blue-700">Name*</div>
                <div className="font-bold text-blue-700">Mobile*</div>
                <div className="font-bold text-blue-700">Joining*</div>
                <div className="font-bold">Deposit</div>
                <div className="font-bold text-amber-700">Due Date</div>
                <div className="font-bold">Rent</div>
                <div>1A-101</div>
                <div>2</div>
                <div>joined</div>
                <div>GUEST 1</div>
                <div>9904048600</div>
                <div>2025-01-02</div>
                <div>10000</div>
                <div className="text-amber-600">2</div>
                <div>12000</div>
              </div>
              <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                <h5 className="font-medium text-sm text-amber-800 mb-1">Due Date & Pro-rated Rent:</h5>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>• <strong>Due Date = Joining Day</strong> (e.g., 2) → No pro-rating, full rent from joining</li>
                  <li>• <strong>Due Date = 1</strong> (1st of month) → Pro-rated rent for remaining days</li>
                  <li>• <strong>Empty</strong> → Defaults to joining day</li>
                </ul>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                <strong>* Required columns.</strong> Rent column is optional - uses bed/room default if empty.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                data-testid="download-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div>
              <Label>Select Guest List Excel File</Label>
              <Input
                ref={guestsInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setGuestsFile(e.target.files?.[0])}
                className="mt-1"
                data-testid="guests-file-input"
              />
              {guestsFile && (
                <p className="text-sm text-blue-600 mt-1">Selected: {guestsFile.name}</p>
              )}
            </div>
            <Button 
              onClick={handleGuestsImport} 
              disabled={!guestsFile || importingType !== null}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="import-guests-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {importingType === 'guests' ? 'Importing Guests...' : 'Import Guest List'}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Import Receipts */}
        <Card className="dash-animate border-2 border-green-500 hover:shadow-md transition-all duration-200" style={{ animationDelay: '80ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <FileSpreadsheet className="h-5 w-5" />
              Import Receipts
            </CardTitle>
            <CardDescription className="text-green-600">
              Upload Receipt List Excel file to import receipt entries into Daily Hisab
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Expected Excel Format:</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• <strong>Column 1:</strong> Date</li>
                <li>• <strong>Column 2:</strong> Room No</li>
                <li>• <strong>Column 3:</strong> Name</li>
                <li>• <strong>Column 4-6:</strong> Cash Rent, Cash Deposit, Cash Electricity</li>
                <li>• <strong>Column 7-9:</strong> Bank Rent, Bank Deposit, Bank Electricity</li>
              </ul>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadReceiptsTemplate}
                className="border-green-500 text-green-600 hover:bg-green-50"
                data-testid="download-receipts-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div>
              <Label>Select Receipt Excel File</Label>
              <Input
                ref={receiptsInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setReceiptsFile(e.target.files?.[0])}
                className="mt-1"
                data-testid="receipts-file-input"
              />
              {receiptsFile && (
                <p className="text-sm text-green-600 mt-1">Selected: {receiptsFile.name}</p>
              )}
            </div>
            <Button 
              onClick={handleReceiptsImport} 
              disabled={!receiptsFile || importingType !== null}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="import-receipts-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {importingType === 'receipts' ? 'Importing...' : 'Import Receipts'}
            </Button>
          </CardContent>
        </Card>

        {/* Import Payments */}
        <Card className="dash-animate border-2 border-red-500 hover:shadow-md transition-all duration-200" style={{ animationDelay: '160ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <FileSpreadsheet className="h-5 w-5" />
              Import Payments
            </CardTitle>
            <CardDescription className="text-red-600">
              Upload Payments Master Excel file to import payment entries
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Expected Excel Format:</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• <strong>Sheet:</strong> "Cash Expense List" for cash payments</li>
                <li>• <strong>Sheet:</strong> "Bank Expense List" for bank payments</li>
                <li>• <strong>Column 1:</strong> Date</li>
                <li>• <strong>Column 2:</strong> Payment Head (Category)</li>
                <li>• <strong>Column 3:</strong> Remark (Description)</li>
                <li>• <strong>Column 4:</strong> Amount (Cash/Bank)</li>
              </ul>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadExpensesTemplate}
                className="border-red-500 text-red-600 hover:bg-red-50"
                data-testid="download-expenses-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div>
              <Label>Select Payment Excel File</Label>
              <Input
                ref={expensesInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setExpensesFile(e.target.files?.[0])}
                className="mt-1"
                data-testid="expenses-file-input"
              />
              {expensesFile && (
                <p className="text-sm text-red-600 mt-1">Selected: {expensesFile.name}</p>
              )}
            </div>
            <Button 
              onClick={handleExpensesImport} 
              disabled={!expensesFile || importingType !== null}
              className="w-full bg-red-600 hover:bg-red-700"
              data-testid="import-expenses-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {importingType === 'expenses' ? 'Importing...' : 'Import Payments'}
            </Button>
          </CardContent>
        </Card>

        {/* Import Electricity Units */}
        <Card className="dash-animate border-2 border-yellow-500 hover:shadow-md transition-all duration-200" style={{ animationDelay: '240ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700">
              <Zap className="h-5 w-5" />
              Import Electricity Units
            </CardTitle>
            <CardDescription className="text-yellow-600">
              Upload electricity meter readings from Excel file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Expected Excel Format (4 columns):</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• <strong>Column 1:</strong> Room (e.g., 1--1)</li>
                <li>• <strong>Column 2:</strong> Month (YYYY-MM format, e.g., 2025-12)</li>
                <li>• <strong>Column 3:</strong> Previous Reading (pre-filled from system, editable)</li>
                <li>• <strong>Column 4:</strong> Current Reading (enter new meter reading)</li>
              </ul>
              <p className="text-xs text-gray-500 mt-2 italic">Note: Download template to get Previous Reading pre-filled from system.</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadElectricityTemplate}
                className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                data-testid="download-electricity-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div>
              <Label>Select Electricity Excel File</Label>
              <Input
                ref={electricityInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setElectricityFile(e.target.files?.[0])}
                className="mt-1"
                data-testid="electricity-file-input"
              />
              {electricityFile && (
                <p className="text-sm text-yellow-600 mt-1">Selected: {electricityFile.name}</p>
              )}
            </div>
            <Button 
              onClick={handleElectricityImport} 
              disabled={!electricityFile || importingType !== null}
              className="w-full bg-yellow-600 hover:bg-yellow-700"
              data-testid="import-electricity-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {importingType === 'electricity' ? 'Importing...' : 'Import Electricity Readings'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Import Instructions</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li><strong>Guest List Import:</strong> Download the template to see available rooms. Fill in guest details (Name, Mobile, Joining Date, Room) and upload. The template includes an "Available Rooms" sheet showing all rooms with vacant beds.</li>
            <li>For Receipts/Payments: Ensure dates are in valid Excel date format</li>
            <li>Make sure there are no merged cells or empty header rows</li>
            <li>The first row should contain column headers</li>
            <li>Select the file and click Import to upload</li>
            <li>Review any errors shown after import</li>
          </ol>
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> Imported data is added to existing records. To replace data, 
              please delete existing entries first from the respective pages.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
